import { ArrowRight, Loader2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

/**
 * Modal listing all four Academy SKUs. `plans` comes from GET /api/academy/plans.
 * Clicking Choose calls `onSelect(planId)` which handles the Stripe checkout redirect
 * (or the founder-cap fallback for unconfigured plans).
 * `onRegisterInterest` shows the interest dialog as an alternative to paying now.
 */
export function AcademyPlanDialog({
    open,
    onOpenChange,
    plans,
    checkoutLoading,
    onSelect,
    onRegisterInterest,
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-weave-bg border-weave-ink max-w-2xl"
                data-testid="academy-plan-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="text-2xl md:text-3xl font-light text-weave-ink">
                        Apply to The Weave Academy
                    </DialogTitle>
                    <DialogDescription className="text-weave-slate">
                        Pick the shape of commitment that fits. Payments are processed
                        securely by Stripe — you'll return here once it's done.
                    </DialogDescription>
                </DialogHeader>
                <div className="mt-2 max-h-[60vh] overflow-y-auto pr-1">
                    {plans.length === 0 ? (
                        <div className="py-8 text-center text-weave-slate">
                            <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                            Loading plans…
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {plans.map((p) => (
                                <div
                                    key={p.id}
                                    className={`p-5 border ${
                                        p.configured
                                            ? "border-weave-ink bg-weave-surface hover:bg-weave-surface-alt"
                                            : "border-weave-border bg-weave-surface-alt/60"
                                    } transition-colors`}
                                    data-testid={`academy-plan-${p.id}`}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <p className="editorial-label text-weave-teal">
                                                {p.price_gbp}
                                            </p>
                                            <p className="text-weave-ink font-medium mt-1">
                                                {p.label}
                                            </p>
                                            <p className="text-sm text-weave-slate mt-2">
                                                {p.blurb}
                                            </p>
                                            {!p.configured && (
                                                <p className="text-[11px] uppercase tracking-editorial text-weave-muted mt-3">
                                                    Not wired to Stripe yet — will open founder-cap.
                                                </p>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => onSelect(p.id)}
                                            disabled={checkoutLoading === p.id}
                                            className="btn-primary shrink-0 whitespace-nowrap"
                                            data-testid={`academy-checkout-${p.id}`}
                                        >
                                            {checkoutLoading === p.id ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    Redirecting…
                                                </>
                                            ) : (
                                                <>
                                                    Choose
                                                    <ArrowRight className="w-4 h-4" />
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <p className="text-xs text-weave-muted mt-3">
                    Prefer to talk first?{" "}
                    <button
                        type="button"
                        onClick={onRegisterInterest}
                        className="weave-link"
                    >
                        Register your interest instead
                    </button>
                    .
                </p>
            </DialogContent>
        </Dialog>
    );
}
